import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Github, ArrowRight, Bell } from 'lucide-react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

const NoticeBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const prefersReduced = useReducedMotion();

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        id="dopove-notice-banner"
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: 1, opacity: 1 }}
        exit={{ scaleY: 0, opacity: 0 }}
        transition={{
          scaleY: { type: prefersReduced ? 'tween' : 'spring', duration: prefersReduced ? 0.1 : 0.3, ease: 'easeOut' },
          opacity: { duration: 0.2 }
        }}
        style={{ transformOrigin: 'top center' }}
        className="relative z-[100] bg-gradient-to-r from-[#0F1219] via-[#1A1F2E] to-[#0F1219] border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col md:flex-row items-center justify-between gap-4">
          
          {/* Cortex Link */}
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-white/5 rounded-lg border border-white/10">
              <Github className="w-4 h-4 text-white" />
            </div>
            <p className="text-[11px] font-medium text-slate-300">
              <span className="text-white font-bold">Cortex</span> is live on GitHub. 
              <a
                href="https://github.com/dopove/cortex"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-blue-400 hover:text-blue-300 underline underline-offset-4 decoration-blue-500/30 hover-scale-press focus-visible-ring transition-[color,transform] duration-200 ease-[var(--ease-out)] font-black uppercase tracking-widest text-[9px]"
              >
                Repo
              </a>
            </p>
          </div>

          {/* Products Status (Plain English) */}
          <div className="flex items-center gap-4 bg-emerald-500/5 px-4 py-1.5 rounded-full border border-emerald-500/10">
            <div className="flex items-center gap-2">
              <Bell className="w-3 h-3 text-emerald-400" />
              <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                ICE V3: OUT NOW
              </p>
            </div>
            <div className="h-3 w-[1px] bg-white/10" />
            <p className="text-[11px] text-slate-300 font-medium">
              ICE V3 is ready and out. <span className="text-white">GoingMerry is coming soon.</span>
            </p>
          </div>

          {/* Right Side: Actions */}
          <div className="flex items-center gap-4">
            {authStatus === 'authenticated' && (
              <button
                onClick={() => window.location.href = '/profile'}
                className="group flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-white hover:text-amber-400 hover-scale-press focus-visible-ring transition-[color,transform] duration-200 ease-[var(--ease-out)]"
              >
                View Profile
                <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-200 ease-[var(--ease-out)]" />
              </button>
            )}
            <button
              type="button"
              aria-label="Close notice"
              onClick={() => setIsVisible(false)}
              className="p-1 hover:bg-white/5 rounded-md hover-scale-press focus-visible-ring transition-[background-color,color,transform] duration-200 ease-[var(--ease-out)] text-slate-500 hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Bottom subtle progress line (Amber for "Coming Soon") */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      </motion.div>
    </AnimatePresence>
  );
};

export default NoticeBanner;
